import { Vector, Matrix, Line } from '../elements/calibrate-photo/sylvester.src';
import { SVD } from '../elements/calibrate-photo/sylvesterSvd';
import { NewtonsOpt1d } from './NewtonsOpt1d';

/**
 * Ensure that the points matrix is valid
 * must have at least 3 points and 2 or 3 dimensions
 * @param {Matrix} pts Point coordinates (NxM) where N is the number of points and M is the number of dimensions
 * @returns {Matrix} Validated points matrix
 */
function _validatePts(pts) {
  if (!(pts instanceof Matrix)) {
    throw new Error('pts must be a Matrix');
  }
  if (pts.rows() < 3) {
    throw new Error('pts must have at least 3 rows');
  }
  if (pts.cols() < 2 || pts.cols() > 3) {
    throw new Error('pts must have 2 or 3 columns');
  }

  return pts;
}

/**
 * Calculate relative distances of points from an anchor point (first point in the matrix)
 * @param {Matrix} pts Sick anchor points (NxM) where N is the number of points and M is the number of dimensions
 * @returns {Vector} Relative distances of the points from the anchor point
 */
function calcRelativeDistances(pts) {
  pts = _validatePts(pts);
  const anchor = pts.row(1);
  // Calculate the relative distances of the points to the anchor point
  let distances = [];
  for (let i = 1; i <= pts.rows(); i++) {
    const pt = pts.row(i);
    distances.push(pt.subtract(anchor).norm());
  }
  const distancesVector = Vector.create(distances);
  const scale = 1 / distancesVector.max();
  const relativeDistances = distancesVector.multiply(scale);

  return relativeDistances;
}

/**
 * Creates a sylvester line through two points
 * @param {Vector|Array} pt1 Coordinates of the first point
 * @param {Vector|Array} pt2 Coordinates of the second point
 * @returns {Line?} Line through the two points
 */
function calcLineThroughPoints(pt1, pt2) {
  // Make sure pt1 and pt2 are valid
  if (pt1 instanceof Array) {
    pt1 = Vector.create(pt1);
  }
  if (pt2 instanceof Array) {
    pt2 = Vector.create(pt2);
  }
  if (!(pt1 instanceof Vector) || !(pt2 instanceof Vector)) {
    throw new Error('Both points must be Vectors or Arrays');
  }
  if (pt1.dimensions() !== pt2.dimensions()) {
    throw new Error('Both points must have the same dimensions');
  }
  if (pt1.dimensions() < 2 || pt1.dimensions() > 3) {
    throw new Error('Both points must be 2D or 3D');
  }
  const a = pt1;
  const b = pt2.subtract(pt1);

  return Line.create(a, b);
}

/**
 * Total least squares fit of a line in xy plane
 * @param {Matrix} pts Points to fit (Nx2) where N is the number of points
 * @returns {object} Mean point and direction vector of the line
 */
function totalLeastSquares(pts) {
  pts = _validatePts(pts);
  //Create a new matrix with the first two columns of pts
  const numPts = pts.rows();
  const pts2d = Matrix.Zero(numPts, 2);
  for (let i = 0; i < numPts; i++) {
    pts2d.set(i, 0, pts.get(i, 0));
    pts2d.set(i, 1, pts.get(i, 1));
  }
  // Calculate the centroid of the points
  const xCentroid = pts2d.col(1).mean(); // One-based index
  const yCentroid = pts2d.col(2).mean(); // One-based index
  const centroid = Vector.create([xCentroid, yCentroid]);
  // Center the points on the centroid
  const tiledCentroid = Matrix.Zero(numPts, 2).map(function (x, i, j) {
    return centroid.e(j);
  });
  const centeredPts = pts2d.subtract(tiledCentroid);
  const { V } = SVD(centeredPts);
  let dir = V.row(2);
  dir = dir.toUnitVector();
  const dir3D = Vector.create([dir.e(2), -dir.e(1), 0]);
  const meanPt = Vector.create([xCentroid, yCentroid, 0]);

  return {
    meanPt,
    dir: dir3D
  };
}

/**
 * Orthographic projection of points to a line.
 * Shortest distance from point to line is calculated.
 * @param {Matrix} pts Points to project (Nx2) where N is the number of points
 * @param {Vector} lineVector 2D vector representing the line
 * @returns {Matrix} Projected points (Nx2) where N is the number of points
 */
function projectPointsToLine(pts, lineVector) {
  pts = _validatePts(pts);
  const unitLineVector = lineVector.toUnitVector();
  const numPts = pts.rows();
  // Take the dot product of each point with the unit line vector
  let dists = [];
  for (let i = 1; i <= numPts; i++) {
    const pt = pts.row(i);
    dists.push(unitLineVector.dot(pt));
  }
  const distsVector = Vector.create(dists);
  // Project the points to the line
  let projectedPts = distsVector.outer(unitLineVector);

  return projectedPts;
}

/**
 * Transforms standard pixel coordinates(0,0) at top left to (0,0) at bottom left
 * Y-axis is inverted from standard pixel coordinates
 * (so that it is consistent with the world coordinate system)
 * @param {Matrix} pixelCoords Pixel coordinates (Nx2) where N is the number of points
 * @param {number} pixelHeight Pixel height of the image
 * @returns {Matrix} Transformed pixel coordinates (Nx2) where N is the number of points
 */
function transformPixelCoords(pixelCoords, pixelHeight) {
  const dimensions = pixelCoords.dimensions();
  let pixelCoordsNew = Matrix.Zero(dimensions.rows, dimensions.cols);
  for (let i = 0; i < dimensions.rows; i++) {
    pixelCoordsNew.set(i, 0, pixelCoords.get(i, 0));
    pixelCoordsNew.set(i, 1, pixelHeight - pixelCoords.get(i, 1));
  }

  return pixelCoordsNew;
}

/**
 * Converts pixel coordinates to actual coordinates on the sensor plane
 * (0, 0, 0) is at the center of the sensor
 * @param {Matrix} pixelCoords Pixel coordinates (Nx2) where N is the number of points
 * @param {number} pixelWidth Width of the image in pixels
 * @param {number} pixelHeight Height of the image in pixels
 * @param {number} sensorWidth Width of the sensor [Length units]
 * @param {number} sensorHeight Height of the sensor [Length units]
 * @returns {Matrix} Sensor Coordinates (Nx3) where N is the number of points
 */
function pixel2SensorCoordinates(pixelCoords, pixelWidth, pixelHeight, sensorWidth, sensorHeight) {
  const newPixelCoords = transformPixelCoords(pixelCoords, pixelHeight);
  const scales = Vector.create([sensorWidth / pixelWidth, sensorHeight / pixelHeight]);
  const numPts = newPixelCoords.rows();
  const worldCoords = Matrix.Zero(numPts, 3);
  for (let i = 0; i < numPts; i++) {
    const worldX = newPixelCoords.get(i, 0) * scales.get(0) - sensorWidth / 2;
    const worldY = newPixelCoords.get(i, 1) * scales.get(1) - sensorHeight / 2;
    worldCoords.set(i, 0, worldX);
    worldCoords.set(i, 1, worldY);
  }

  return worldCoords;
}

/**
 * @typedef {Object} Rectified2DSpace
 * @property {Matrix} rectifiedPts Anchors of the stick in the rectified 2d space (Nx2) where N is the number of anchors
 * @property {Vector} rectifiedFocalPt Focal point in the rectified 2d space
 */

/**
 * Transforms pixel coordinates to rectified 2d space
 * The 2d space is the plane regressed through the stick anchors on the sensor plane and the focal point
 * @param {Matrix} pixelCoords Pixel coordinates of the stick anchors (Nx2) where N is the number of anchors
 * @param {number} focalLength Focus length of the camera
 * @param {number} sensorWidth Width of the sensor [Length units]
 * @param {number} sensorHeight Height of the sensor [Length units]
 * @param {number} pixelWidth Pixel width of the image
 * @param {number} pixelHeight Pixel height of the image
 * @returns {Rectified2DSpace} Rectified 2d space coordinates and focal point
 */
function transform2Rectified2DSpace(pixelCoords, focalLength, sensorWidth, sensorHeight, pixelWidth, pixelHeight) {
  const imageCoords = pixel2SensorCoordinates(pixelCoords, pixelWidth, pixelHeight, sensorWidth, sensorHeight);
  const focalLengthCoords = Vector.create([0, 0, focalLength]);
  const numPts = imageCoords.rows();
  // Fit line to points
  const { meanPt, dir } = totalLeastSquares(imageCoords);
  const basis0 = dir;
  const primaryFocalRay = focalLengthCoords.subtract(meanPt).toUnitVector();

  // Project points to line before tansformation to new coordinate system
  const tiledMean = Matrix.Zero(numPts, 3).map(function (x, i, j) {
    return meanPt.e(j);
  });
  let tiledMean2d = Matrix.Zero(numPts, 2).map(function (x, i, j) {
    return meanPt.e(j);
  });
  let meanCenteredPts = imageCoords.subtract(tiledMean);
  // Get the first two columns of the mean centered points
  let meanCenteredPts2d = Matrix.Zero(numPts, 2);
  for (let i = 0; i < numPts; i++) {
    meanCenteredPts2d.set(i, 0, meanCenteredPts.get(i, 0));
    meanCenteredPts2d.set(i, 1, meanCenteredPts.get(i, 1));
  }
  const basis0Projected = Vector.create([basis0.e(1), basis0.e(2)]);
  const projectedPts2d = projectPointsToLine(meanCenteredPts2d, basis0Projected).add(tiledMean2d);
  let projectedPts3d = Matrix.Zero(numPts, 3);
  for (let i = 0; i < numPts; i++) {
    projectedPts3d.set(i, 0, projectedPts2d.get(i, 0));
    projectedPts3d.set(i, 1, projectedPts2d.get(i, 1));
  }
  const basis1 = primaryFocalRay.cross(basis0).toUnitVector();
  let basis2 = basis0.cross(basis1).toUnitVector(); // Might be negated depending on the determinant of the rotation matrix

  let R = Matrix.create([
    [basis2.e(1), basis0.e(1), basis1.e(1)],
    [basis2.e(2), basis0.e(2), basis1.e(2)],
    [basis2.e(3), basis0.e(3), basis1.e(3)]
  ]);
  const preservesRightHandedness = R.determinant() > 0;
  if (!preservesRightHandedness) {
    basis2 = basis2.multiply(-1);
  }
  const T = Matrix.create([
    [basis2.e(1), basis0.e(1), basis1.e(1), meanPt.e(1)],
    [basis2.e(2), basis0.e(2), basis1.e(2), meanPt.e(2)],
    [basis2.e(3), basis0.e(3), basis1.e(3), meanPt.e(3)],
    [0, 0, 0, 1]
  ]);

  // Make homogenous coordinates from projected points 3d
  let rectifiedPts = Matrix.Zero(4, numPts);
  for (let i = 0; i < numPts; i++) {
    rectifiedPts.set(0, i, projectedPts3d.get(i, 0));
    rectifiedPts.set(1, i, projectedPts3d.get(i, 1));
    rectifiedPts.set(2, i, 0);
    rectifiedPts.set(3, i, 1);
  }
  const Tinv = T.inverse();
  rectifiedPts = Tinv.multiply(rectifiedPts);
  rectifiedPts = Matrix.create([rectifiedPts.row(1).elements, rectifiedPts.row(2).elements]).transpose();
  const homogeneousFocalPt = Vector.create([...focalLengthCoords.elements, 1]);
  let rectifiedFocalPt = Tinv.multiply(homogeneousFocalPt);
  rectifiedFocalPt = Vector.create([rectifiedFocalPt.e(1), rectifiedFocalPt.e(2)]);

  return {
    rectifiedPts,
    rectifiedFocalPt
  };
}

/**
 * @typedef {Object} CameraOrientation
 * @property {number} bestAngle Angle of the camera to the stick
 * @property {number} distToStickBottom Distance from the camera to the bottom of the stick
 * @property {number} relativeHeightOfCamera Height of the camera relative to the lowest anchor of the stick
 * @property {number} lowerAngleOfAttack Lower angle of attack of the camera
 * @property {number} upperAngleOfAttack Upper angle of attack of the camera
 */

/**
 * Calculates the 2d orientation of the camera to the stick
 * The 2d plane is the plane regressed through the stick anchors and the focal point
 * @param {Matrix} rectifiedPts Anchors of the stick in the rectified 2d space (Nx2) where N is the number of anchors
 * @param {Vector} rectifiedFocalPt Focal point in the rectified 2d space
 * @param {Vector} correspondingHeights Heights of the stick anchors
 * @returns {CameraOrientation} Camera orientation
 */
function calcOrientation(rectifiedPts, rectifiedFocalPt, correspondingHeights, derivativeOptions = {}) {
  // Create rays from the rectified points to the rectified focal point
  let rays = [];
  for (let i = 1; i <= rectifiedPts.rows(); i++) {
    const pt = rectifiedPts.row(i);
    const ray = calcLineThroughPoints(rectifiedFocalPt, pt);
    rays.push(ray);
  }
  const numAnchors = correspondingHeights.dimensions();
  let correspondingHeights2d = Matrix.Zero(numAnchors, 2);
  for (let i = 0; i < numAnchors; i++) {
    correspondingHeights2d.set(i, 1, correspondingHeights.get(i));
  }
  const groundTruthDists = calcRelativeDistances(correspondingHeights2d);
  const objective = (angle) => {
    const proposedStickLine = calcLineThroughPoints(Vector.Zero(2), Vector.create([Math.sin(angle), Math.cos(angle)]));
    let proposedIntersections = [];
    for (let i = 0; i < rays.length; i++) {
      const intersection = proposedStickLine.intersectionWith(rays[i]);
      proposedIntersections.push(intersection.elements);
    }
    const proposedDists = calcRelativeDistances(Matrix.create(proposedIntersections));
    const residuals = proposedDists.subtract(groundTruthDists);
    const squaredResiduals = residuals.map(function (x) {
      return x ** 2;
    }).elements;
    const sumSquaredResiduals = squaredResiduals.reduce((a, b) => a + b, 0);

    return sumSquaredResiduals;
  };
  const res = NewtonsOpt1d(objective, 0, 1e-6, 100, derivativeOptions);
  const bestAngle = res.xMin;

  // Solve scales by similar triangles
  const proposedStickOrigin = rectifiedPts.row(1);
  const proposedStickOrigin3d = Vector.create([...proposedStickOrigin.elements, 0]);
  const bestStickVector = Vector.create([Math.sin(bestAngle), Math.cos(bestAngle)]).add(proposedStickOrigin);
  const lastRay = rays[rays.length - 1];
  const stickLine = calcLineThroughPoints(proposedStickOrigin, bestStickVector);
  const bestStickTop = stickLine.intersectionWith(lastRay);

  const B = proposedStickOrigin3d.subtract(bestStickTop).norm();
  const lastCorrespondingHeight = correspondingHeights.e(correspondingHeights.dimensions());
  const firstCorrespondingHeight = correspondingHeights.e(1);
  const Bprime = lastCorrespondingHeight - firstCorrespondingHeight;
  const vectorFromFocalToStickOrigin = proposedStickOrigin.subtract(rectifiedFocalPt);
  const A = vectorFromFocalToStickOrigin.norm();
  const scale = Bprime / B;
  const distToStickBottom = scale * A;
  const relativeHeight = scale * vectorFromFocalToStickOrigin.elements[1];

  // Solve interior angles of the triangle
  const rectifiedFocalPt3d = Vector.create([...rectifiedFocalPt.elements, 0]);
  const C = bestStickTop.subtract(rectifiedFocalPt3d).norm();
  const ABIncludedAngle = Math.acos((A ** 2 + B ** 2 - C ** 2) / (2 * A * B));
  const BCIncludedAngle = Math.acos((B ** 2 + C ** 2 - A ** 2) / (2 * B * C));

  return {
    bestAngle,
    distToStickBottom,
    relativeHeightOfCamera: relativeHeight,
    lowerAngleOfAttack: ABIncludedAngle,
    upperAngleOfAttack: BCIncludedAngle
  };
}

/**
 * Calculates the camera orientation given pixel coordinates and corresponding heights of stick anchors
 * Camera location is relative to the lowest anchor of the stick
 * @param {Array} anchors Stick anchors with pixel coordinates and heights
 * @param {number} focalLength Focal length of the camera [Length units]
 * @param {number} sensorWidth Width of the sensor [Length units]
 * @param {number} sensorHeight Height of the sensor [Length units]
 * @param {number} pixelWidth Pixel width of the image
 * @param {number} pixelHeight Pixel height of the image
 * @param {object} derivativeOptions Options for the derivative estimation
 * @returns {CameraOrientation} Camera orientation
 */
export function CalcCameraOrientation(anchors, focalLength, sensorWidth, sensorHeight, pixelWidth, pixelHeight, derivativeOptions = {}) {
  let pixelCoords = [];
  let correspondingHeights = [];
  for (const anchor of anchors) {
    pixelCoords.push([anchor.x, anchor.y]);
    correspondingHeights.push(anchor.height);
  }
  const pixelCoordsMatrix = Matrix.create(pixelCoords);
  const correspondingHeightsVector = Vector.create(correspondingHeights);

  const { rectifiedPts, rectifiedFocalPt } = transform2Rectified2DSpace(
    pixelCoordsMatrix,
    focalLength,
    sensorWidth,
    sensorHeight,
    pixelWidth,
    pixelHeight
  );
  const res = calcOrientation(rectifiedPts, rectifiedFocalPt, correspondingHeightsVector, derivativeOptions);

  return res;
}
